import { useEffect, useRef, useState, type FormEvent } from "react";

const turnstileSiteKey = import.meta.env.VITE_TURNSTILE_SITE_KEY as string | undefined;

declare global {
  interface Window {
    turnstile?: {
      render: (container: HTMLElement, options: Record<string, unknown>) => string;
      remove: (widgetId: string) => void;
      reset: (widgetId?: string) => void;
    };
  }
}

function Brand() {
  return (
    <a className="brand" href="/" aria-label="NebenkostenEule.de Startseite">
      <img src="/logo.svg" alt="" width="38" height="38" />
      <span>
        <span>Nebenkosten</span>
        <span className="brand-accent">Eule</span>
        <span>.de</span>
      </span>
    </a>
  );
}

function usePageMetadata(title: string, noIndex = false) {
  useEffect(() => {
    document.title = title;
    let robots = document.querySelector<HTMLMetaElement>('meta[name="robots"]');
    if (noIndex && !robots) {
      robots = document.createElement("meta");
      robots.name = "robots";
      document.head.appendChild(robots);
    }
    if (robots) robots.content = noIndex ? "noindex, nofollow" : "index, follow";
  }, [noIndex, title]);
}

function Header() {
  return (
    <header className="site-header">
      <div className="header-inner">
        <Brand />
      </div>
    </header>
  );
}

function WaitlistForm() {
  const [status, setStatus] = useState<"idle" | "sending" | "success" | "error">("idle");
  const [message, setMessage] = useState("");
  const [turnstileRequested, setTurnstileRequested] = useState(false);
  const [turnstileReady, setTurnstileReady] = useState(false);
  const submitting = useRef(false);
  const turnstileContainer = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!turnstileSiteKey || !turnstileRequested) return;
    let widgetId: string | undefined;

    const renderWidget = () => {
      if (!window.turnstile || !turnstileContainer.current || turnstileContainer.current.childElementCount) return;
      widgetId = window.turnstile.render(turnstileContainer.current, {
        sitekey: turnstileSiteKey,
        size: "flexible",
        theme: "light",
        language: "de",
        action: "waitlist-signup",
        feedbackEnabled: false,
        callback: () => setTurnstileReady(true),
        "expired-callback": () => setTurnstileReady(false),
        "error-callback": () => setTurnstileReady(false)
      });
    };

    const scriptId = "cloudflare-turnstile-script";
    let script = document.getElementById(scriptId) as HTMLScriptElement | null;
    if (!script) {
      script = document.createElement("script");
      script.id = scriptId;
      script.src = "https://challenges.cloudflare.com/turnstile/v0/api.js?render=explicit";
      script.async = true;
      script.defer = true;
      document.head.appendChild(script);
    }
    script.addEventListener("load", renderWidget);
    renderWidget();

    return () => {
      script?.removeEventListener("load", renderWidget);
      if (widgetId && window.turnstile) window.turnstile.remove(widgetId);
    };
  }, [turnstileRequested]);

  async function handleSubmit(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    if (submitting.current) return;
    submitting.current = true;
    const form = event.currentTarget;
    const formData = new FormData(form);
    setStatus("sending");
    setMessage("");

    try {
      const response = await fetch("/api/waitlist", {
        method: "POST",
        headers: { "content-type": "application/json" },
        body: JSON.stringify({
          email: formData.get("email"),
          consent: formData.get("consent") === "on",
          website: formData.get("website"),
          turnstileToken: formData.get("cf-turnstile-response")
        })
      });
      if (!response.ok) {
        const result = await response.json().catch(() => ({})) as { error?: string };
        throw new Error(result.error || "Die Anmeldung konnte nicht gesendet werden.");
      }
      const result = await response.json() as { ok?: boolean; error?: string };
      if (!result.ok) throw new Error(result.error || "Die Anmeldung konnte nicht gesendet werden.");
      form.reset();
      setStatus("success");
      setMessage("Fast geschafft: Bitte bestätige deine Anmeldung über den Link in der E-Mail.");
    } catch (error) {
      setStatus("error");
      setMessage(error instanceof Error ? error.message : "Bitte versuche es gleich noch einmal.");
      setTurnstileReady(false);
      window.turnstile?.reset();
    } finally {
      submitting.current = false;
    }
  }

  if (status === "success") {
    return (
      <div className="form-success" role="status">
        <div className="success-heading">
          <span className="success-icon" aria-hidden="true">
            <svg viewBox="0 0 24 24"><path d="m5 12 4 4L19 6" /></svg>
          </span>
          <h2>Schau in dein Postfach</h2>
        </div>
        <p>{message}</p>
        <p className="success-note">Ohne Bestätigung wirst du nicht in die Warteliste aufgenommen.</p>
      </div>
    );
  }

  return (
    <form className="waitlist-form" onSubmit={handleSubmit}>
      <div>
        <h2>Sei von Anfang an dabei.</h2>
        <p className="form-copy">Wir schreiben dir, sobald NebenkostenEule bereit ist.</p>
      </div>

      <label className="field-label" htmlFor="email">E-Mail-Adresse</label>
      <input
        id="email"
        name="email"
        type="email"
        inputMode="email"
        autoComplete="email"
        placeholder="deine@email.de"
        maxLength={254}
        required
        onChange={(event) => {
          if (event.currentTarget.value.trim()) setTurnstileRequested(true);
        }}
      />

      <label className="honeypot" aria-hidden="true" inert>
        Website
        <input name="website" type="text" tabIndex={-1} autoComplete="off" />
      </label>

      <label className="consent">
        <input name="consent" type="checkbox" required />
        <span>
          Ich möchte bis zu drei E-Mails zum Start von NebenkostenEule erhalten. Meine Einwilligung kann ich jederzeit
          widerrufen. Weitere Informationen stehen im <a href="/datenschutz">Datenschutz</a>.
        </span>
      </label>

      <div className="turnstile-slot">
        {turnstileSiteKey && turnstileRequested ? (
          <div className="cf-turnstile" ref={turnstileContainer} />
        ) : !turnstileSiteKey ? (
          <p className="config-note" role="alert">Der Spamschutz ist noch nicht konfiguriert.</p>
        ) : null}
      </div>

      <button
        className="primary-button"
        type="submit"
        disabled={status === "sending" || !turnstileSiteKey || !turnstileReady}
      >
        {status === "sending" ? "Wird eingetragen …" : "Start nicht verpassen"}
        {status !== "sending" ? (
          <svg viewBox="0 0 24 24" aria-hidden="true"><path d="m9 18 6-6-6-6" /></svg>
        ) : null}
      </button>

      <p className="form-meta">
        Double-Opt-in · jederzeit abmeldbar · keine Weitergabe für fremde Werbung
      </p>
      {status === "error" ? <p className="form-error" role="alert">{message}</p> : null}
    </form>
  );
}

const benefits = [
  {
    title: "Abrechnung verständlich prüfen",
    copy: "NebenkostenEule erklärt Kostenpositionen, Rechenwege und mögliche Auffälligkeiten."
  },
  {
    title: "Konkrete nächste Schritte",
    copy: "Du erhältst nachvollziehbare Hinweise statt einer unübersichtlichen Sammlung von Zahlen."
  },
  {
    title: "Deine Unterlagen im Mittelpunkt",
    copy: "Die Prüfung orientiert sich an deiner Abrechnung und den von dir bereitgestellten Angaben."
  }
];

function LandingPage() {
  usePageMetadata("NebenkostenEule | Nebenkostenabrechnung verständlich prüfen");

  return (
    <>
      <Header />
      <main className="landing-main">
        <section className="hero" aria-labelledby="hero-title">
          <img className="hero-background" src="/hero-image.webp" alt="" />
          <div className="container hero-grid">
            <div className="hero-copy">
              <h1 id="hero-title">
                Nebenkostenabrechnung<br />
                <span>endlich verständlich.</span>
              </h1>
              <p className="intro">
                Bald kannst du deine Nebenkostenabrechnung mit NebenkostenEule KI-gestützt prüfen und dir auffällige
                Kosten verständlich erklären lassen.
              </p>
              <ul className="benefit-list" aria-label="Das bietet NebenkostenEule">
                {benefits.map((benefit) => (
                  <li key={benefit.title}>
                    <span className="check-icon" aria-hidden="true">
                      <svg viewBox="0 0 24 24"><path d="m6 12 4 4 8-9" /></svg>
                    </span>
                    <span><strong>{benefit.title}</strong><small>{benefit.copy}</small></span>
                  </li>
                ))}
              </ul>
            </div>

            <div className="form-stage">
              <img className="mascot" src="/mascots/eule-calm.svg" alt="" />
              <WaitlistForm />
            </div>
          </div>
        </section>
      </main>
      <Footer />
    </>
  );
}

function LegalLayout({ title, children }: { title: string; children: React.ReactNode }) {
  return (
    <>
      <Header />
      <main className="container legal-page">
        <h1>{title}</h1>
        {children}
      </main>
      <Footer />
    </>
  );
}

function ImpressumPage() {
  usePageMetadata("Impressum | NebenkostenEule", true);

  return (
    <LegalLayout title="Impressum">
      <p>Angaben gemäß § 5 Digitale-Dienste-Gesetz (DDG)</p>
      <h2>Anbieter</h2>
      <p>Real Movement Analytics GmbH<br />Kleines Everstal 18d<br />44388 Dortmund<br />Deutschland</p>
      <h2>Kontakt</h2>
      <p>Telefon: 0231 / 58 69 28 61<br />E-Mail: <a href="mailto:info@rm-analytics.de">info@rm-analytics.de</a></p>
      <h2>Vertretungsberechtigte Person</h2>
      <p>Geschäftsführer: Johann Martens</p>
      <h2>Registereintrag</h2>
      <p>Handelsregister: Amtsgericht Dortmund<br />Registernummer: HRB 25130</p>
      <h2>Umsatzsteuer-ID</h2>
      <p>Umsatzsteuer-Identifikationsnummer gemäß § 27a UStG: DE 286 244 932</p>
      <h2>Verantwortlich für journalistisch-redaktionelle Inhalte</h2>
      <p>Johann Martens<br />Kleines Everstal 18d<br />44388 Dortmund</p>
      <h2>Verbraucherstreitbeilegung</h2>
      <p>Wir sind weder verpflichtet noch bereit, an Streitbeilegungsverfahren vor einer Verbraucherschlichtungsstelle teilzunehmen.</p>
      <h2>Hinweis zur geplanten Leistung</h2>
      <p>NebenkostenEule ist noch nicht verfügbar. Geplant ist eine automatisierte, KI-gestützte Dokumentenprüfung. Sie ersetzt keine individuelle Rechtsberatung.</p>
    </LegalLayout>
  );
}

function DatenschutzPage() {
  usePageMetadata("Datenschutzerklärung | NebenkostenEule", true);

  return (
    <LegalLayout title="Datenschutzerklärung">
      <p>Stand: 23. Juli 2026</p>
      <h2>1. Verantwortliche Stelle</h2>
      <p>Real Movement Analytics GmbH<br />Kleines Everstal 18d<br />44388 Dortmund<br />Deutschland<br />E-Mail: <a href="mailto:info@rm-analytics.de">info@rm-analytics.de</a><br />Telefon: 0231 / 58 69 28 61</p>
      <h2>2. Zweck und Umfang der Verarbeitung</h2>
      <p>Auf dieser vorläufigen Website kannst du dich mit deiner E-Mail-Adresse darüber informieren lassen, sobald NebenkostenEule startet. Wir erheben über das Formular ausschließlich deine E-Mail-Adresse, deine Einwilligung und die technisch erforderlichen Versand- und Bestätigungsdaten.</p>
      <h2>3. Rechtsgrundlagen</h2>
      <p>Die Verarbeitung für die Warteliste und die Startbenachrichtigungen erfolgt auf Grundlage deiner Einwilligung nach Art. 6 Abs. 1 lit. a DSGVO. Die technische Bereitstellung und Absicherung der Website erfolgt auf Grundlage unseres berechtigten Interesses an einem sicheren und funktionsfähigen Internetauftritt nach Art. 6 Abs. 1 lit. f DSGVO.</p>
      <h2>4. Hosting durch Cloudflare</h2>
      <p>Die Website wird über Cloudflare Pages bereitgestellt. Beim Aufruf können technisch notwendige Verbindungsdaten wie IP-Adresse, Zeitpunkt, angeforderte Adresse, Browserinformationen und Sicherheitsereignisse verarbeitet werden. Beim Absenden des Formulars verarbeitet eine Cloudflare Pages Function die E-Mail-Adresse vorübergehend und übermittelt sie nach erfolgreicher Sicherheitsprüfung an Brevo. Eine eigene Cloudflare-Datenbank für E-Mail-Adressen verwenden wir nicht. Dies dient der Auslieferung, Stabilität und Absicherung der Website.</p>
      <h2>5. Spamschutz mit Cloudflare Turnstile</h2>
      <p>Das Formular nutzt Cloudflare Turnstile, um automatisierte und missbräuchliche Anmeldungen zu erkennen. Turnstile wird erst geladen, nachdem du mit der Eingabe einer E-Mail-Adresse beginnst. Dabei verarbeitet Cloudflare technische Signale des Browsers und Geräts sowie Verbindungsdaten. Der erzeugte, kurzzeitig gültige Sicherheitsnachweis wird beim Absenden serverseitig geprüft. Die optionale Pre-Clearance-Funktion wird nicht verwendet.</p>
      <h2>6. Warteliste und Double-Opt-in mit Brevo</h2>
      <p>Für die Verwaltung der Warteliste und den E-Mail-Versand nutzen wir Brevo. Nach der Anmeldung sendet Brevo eine Bestätigungs-E-Mail. Erst nach dem Klick auf den Bestätigungslink wird die Anmeldung abgeschlossen. Dabei verarbeitet Brevo insbesondere E-Mail-Adresse, Listenstatus, Einwilligungs- und Bestätigungszeitpunkte sowie technische Versanddaten in unserem Auftrag.</p>
      <p>Die Einwilligung umfasst höchstens drei E-Mails rund um den Start von NebenkostenEule. Wir verwenden die Adresse nicht für andere Werbung und geben sie nicht für fremde Werbezwecke weiter.</p>
      <h2>7. Speicherdauer und Widerruf</h2>
      <p>Bestätigte Wartelistendaten werden spätestens 30 Tage nach der dritten und letzten Startbenachrichtigung gelöscht. Der Bestätigungslink für eine noch nicht bestätigte Anmeldung ist 30 Tage gültig; nicht bestätigte Adressen werden nicht in die Warteliste aufgenommen. Technische Versand- und Sicherheitsprotokolle werden nur so lange gespeichert, wie dies für Betriebssicherheit, Missbrauchsschutz und den Nachweis der Einwilligung erforderlich ist. Du kannst deine Einwilligung jederzeit über den Abmeldelink in jeder Start-E-Mail oder per E-Mail an <a href="mailto:hallo@nebenkosteneule.de">hallo@nebenkosteneule.de</a> widerrufen. Die Rechtmäßigkeit der Verarbeitung bis zum Widerruf bleibt unberührt.</p>
      <h2>8. Cookies, Analyse und Reichweitenmessung</h2>
      <p>Wir setzen keine Analyse-, Marketing- oder Reichweitenmessungsdienste ein. Einwilligungsbedürftige Cookies werden durch diese Website nicht gesetzt. Technisch erforderliche Speicherungen durch die eingebundenen Sicherheits- und Hostingdienste bleiben hiervon unberührt.</p>
      <h2>9. Empfänger und Auftragsverarbeitung</h2>
      <p>Empfänger personenbezogener Daten sind Cloudflare für Hosting und Spamschutz sowie Brevo für Double-Opt-in, Kontaktverwaltung und E-Mail-Versand. Beide Dienstleister verarbeiten Daten in unserem Auftrag auf Grundlage von Vereinbarungen zur Auftragsverarbeitung.</p>
      <h2>10. Datenübermittlung in Drittländer</h2>
      <p>Bei Cloudflare und Brevo kann eine Verarbeitung außerhalb des Europäischen Wirtschaftsraums nicht vollständig ausgeschlossen werden. Soweit dabei personenbezogene Daten in ein Drittland übermittelt werden, erfolgt dies auf Grundlage eines Angemessenheitsbeschlusses, geeigneter Garantien wie den EU-Standardvertragsklauseln oder einer anderen gesetzlichen Grundlage.</p>
      <h2>11. Freiwilligkeit der Angaben</h2>
      <p>Die Angabe deiner E-Mail-Adresse und die Einwilligung sind freiwillig. Ohne diese Angaben können wir dich nicht über den Start von NebenkostenEule informieren.</p>
      <h2>12. Deine Rechte</h2>
      <p>Du hast nach Maßgabe der DSGVO Rechte auf Auskunft, Berichtigung, Löschung, Einschränkung der Verarbeitung, Datenübertragbarkeit und Widerspruch. Eine erteilte Einwilligung kannst du jederzeit mit Wirkung für die Zukunft widerrufen.</p>
      <h2>13. Automatisierte Entscheidungen</h2>
      <p>Eine automatisierte Entscheidungsfindung einschließlich Profiling im Sinne von Art. 22 DSGVO findet nicht statt.</p>
      <h2>14. Beschwerderecht</h2>
      <p>Du hast das Recht, dich bei einer Datenschutzaufsichtsbehörde zu beschweren. Zuständig für uns ist insbesondere die Landesbeauftragte für Datenschutz und Informationsfreiheit Nordrhein-Westfalen.</p>
      <h2>15. Änderungen</h2>
      <p>Wir passen diese Datenschutzerklärung an, wenn sich die Website, eingesetzte Dienstleister oder rechtliche Anforderungen ändern.</p>
    </LegalLayout>
  );
}

function ConfirmedPage() {
  usePageMetadata("E-Mail-Adresse bestätigt | NebenkostenEule", true);

  return (
    <>
      <Header />
      <main className="confirmation-page">
        <div className="confirmation-card">
          <img src="/mascots/eule-hurray.svg" alt="" width="150" height="150" />
          <span className="success-icon" aria-hidden="true">
            <svg viewBox="0 0 24 24"><path d="m5 12 4 4L19 6" /></svg>
          </span>
          <h1>Du bist dabei.</h1>
          <p>Deine E-Mail-Adresse ist bestätigt. Wir informieren dich, sobald NebenkostenEule startet.</p>
          <a className="secondary-button" href="/">Zurück zur Startseite</a>
        </div>
      </main>
      <Footer />
    </>
  );
}

function Footer() {
  return (
    <footer className="site-footer">
      <div className="container footer-inner">
        <p className="legal-note">NebenkostenEule ersetzt keine individuelle Rechtsberatung.</p>
        <nav aria-label="Rechtliches">
          <a href="/impressum">Impressum</a>
          <a href="/datenschutz">Datenschutz</a>
          <a href="mailto:hallo@nebenkosteneule.de">Kontakt</a>
        </nav>
      </div>
    </footer>
  );
}

export function App() {
  const path = window.location.pathname.replace(/\/+$/, "") || "/";
  if (path === "/impressum") return <ImpressumPage />;
  if (path === "/datenschutz") return <DatenschutzPage />;
  if (path === "/bestaetigt") return <ConfirmedPage />;
  return <LandingPage />;
}
